import PlaySVG from '@public/icons/play2.svg';
import PointerCrossSVG from '@public/icons/pointer-cross.svg';
import { useGLTF } from '@react-three/drei';
import classNames from 'classnames';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { HTMLAttributes, useEffect, useState } from 'react';
import { useMediaQueryDeviceStateValue } from '@/shared/atoms/media-query-device';
import { useOpenedPopupsValue, useSetOpenedPopupsState } from '@/shared/atoms/opened-popups';
import { usePavilionsValue } from '@/shared/atoms/pavilionsData';
import CloseButton from '@/shared/components/configurator/CloseButton';
import Configurator from '@/shared/components/configurator/Configurator';
import ConstructorButton from '@/shared/components/configurator/ConstructorButton';
import {
    useCurrentPavilionSetState,
    useCurrentPavilionValue,
} from '@/shared/components/configurator/atoms/current-pavilion';
import { useHintVisibleValue } from '@/shared/components/configurator/atoms/hint-visible';
import { useIsModelShownSetState, useIsModelShownValue } from '@/shared/components/configurator/atoms/is-model-shown';
import Popup from '@/shared/components/shared/Popup';
import { CONFIGURATOR_WALL_COLOR_DEFAULT } from '@/shared/config/const';
import { useMounted } from '@/shared/hooks/use-mounted';
import { tp } from '@/shared/lib/typograf';
import FormPopup from './FormPopup/FormPopup';
import MapFilters from './InhouseMap/MapFilters';
import { MapStateContext, Spaces, StandBookingType, StandType } from './InhouseMap/context';
import { MODEL_URL as STAND_MODEL_URL } from './StandPicker/StandPicker';
import StandPopup from './StandPopup';
import TurnDeviceMessage from './TurnDeviceMessage';
import { useSetCurrentModelState } from './atoms/current-model-state';
import {
    cameraViewTypes,
    useSetStandPickerCameraViewState,
    useStandPickerCameraViewValue,
} from './atoms/stand-picker-camera-view';

export const INHOUSE_MAP_NAME = 'inhouse-map';

const WebGL = dynamic(() => import('@/shared/components/configurator/webgl/WebGL'), { ssr: false });

const TourBtn = (props: HTMLAttributes<HTMLElement>) => {
    return (
        <ConstructorButton
            {...props}
            tag="a"
            href="https://kuula.co/post/n1/collection/7ZPQg"
            target="_blank"
            rel="noreferrer nofollow"
            variant="white"
            className={classNames('pavilion-tour-btn pointer-events-auto', props.className)}
            icon={<PlaySVG style={{ position: 'relative', top: -1 }} />}
        >
            Виртуальный тур
        </ConstructorButton>
    );
};
const InhouseMapPopup = () => {
    const { openPopup, closePopup } = useSetOpenedPopupsState();
    const openedPopups = useOpenedPopupsValue();
    const isMapPopupOpen = openedPopups.includes(INHOUSE_MAP_NAME);
    const currentPavilion = useCurrentPavilionValue();
    const setCurrentPavilion = useCurrentPavilionSetState();
    const setCurrentModelState = useSetCurrentModelState();
    const isModelShown = useIsModelShownValue();
    const hintVisible = useHintVisibleValue();
    const setIsModelShown = useIsModelShownSetState();
    const router = useRouter();
    const [activePavilionSlug, setActivePavilionSlug] = useState('');
    const standPickerCameraView = useStandPickerCameraViewValue();
    const setStandPickerCameraView = useSetStandPickerCameraViewState();
    const device = useMediaQueryDeviceStateValue();
    const isDesktop = device === 'desktop';
    const isMounted = useMounted();

    const [space, setSpace] = useState<Spaces>(0);
    const [type, setType] = useState<StandType>('individual');
    const [bookingType, setBookingType] = useState<StandBookingType>('unset');
    const { pavilionData, pavilionDataLoading, pavilionDataError } = usePavilionsValue();
    /**
     * Показываем 3D модель с выбранной конфигурацией, если есть стейт в URL
     */
    useEffect(() => {
        if (typeof router.query.slug === 'string') {
            openPopup(INHOUSE_MAP_NAME);
            setIsModelShown(true);
        }
    }, [openPopup, router.query.slug, setIsModelShown]);

    useEffect(() => {
        if (isModelShown) {
            setActivePavilionSlug('');
        }
    }, [isModelShown]);

    /**
     * Prefetch stand picker GLTF
     */
    useEffect(() => {
        useGLTF.preload(STAND_MODEL_URL);
    }, []);

    return (
        <Popup name={INHOUSE_MAP_NAME} className="constructor-popup" data-popup-preset="default">
            {isMapPopupOpen && (
                <MapStateContext.Provider value={{ space, setSpace, type, setType, bookingType, setBookingType }}>
                    {!isModelShown && (
                        <>
                            <div className="page-top">
                                <div className="h2">Павильон</div>
                                <CloseButton
                                    text="К расположению"
                                    className="page-top__close-btn"
                                    onClick={() => {
                                        closePopup(INHOUSE_MAP_NAME);
                                    }}
                                />
                                <div className="pavilion-hint text-l">Выберите стенд</div>
                            </div>

                            <div className="inhouse-map-bottom pointer-events-none wrapper">
                                {/* <TourBtn className="pointer-events-auto" /> */}
                                <MapFilters cameraPosition className="pointer-events-auto" />
                            </div>

                            <StandPopup
                                standsData={Object.values(pavilionData.data)}
                                pavilionSlug={activePavilionSlug}
                                setPavilionSlug={setActivePavilionSlug}
                            />
                        </>
                    )}

                    <div className="webgl-wrapper">
                        <WebGL activePavilionSlug={activePavilionSlug} setActivePavilionSlug={setActivePavilionSlug} />
                    </div>

                    <TurnDeviceMessage />
                </MapStateContext.Provider>
            )}

            {isModelShown && (
                <>
                    <div className="contstructor-content">
                        <div className="page-top">
                            <div className="h3 pointer-events-all">{currentPavilion}</div>
                            <CloseButton
                                text="К схеме"
                                onClick={() => {
                                    setIsModelShown(false);
                                    setCurrentPavilion(null);

                                    setCurrentModelState({
                                        color: {
                                            title: CONFIGURATOR_WALL_COLOR_DEFAULT,
                                            value: [null, null, null, null, null],
                                        },
                                        openedWallColorPickerIndex: null,
                                        reception: {
                                            title: 'Большая',
                                            value: 'type-1-big',
                                        },
                                    });

                                    router.replace({ href: router.asPath, query: {} });
                                }}
                                className="pointer-events-all page-top__close-btn configurator-page-close-btn"
                            />
                        </div>
                        {hintVisible && (
                            <div className="configurator-hint">
                                <div className="configurator-hint__icon">
                                    <PointerCrossSVG />
                                </div>
                                <div className="configurator-hint-desktop-text text-s">
                                    {tp('Зажмите мышь для изменения ракурса обзора конфигурации')}
                                </div>
                                <div className="configurator-hint-mobile-text text-xxs">
                                    {tp('Разверните модель, чтобы рассмотреть стенд')}
                                </div>
                            </div>
                        )}
                    </div>

                    <Configurator />
                </>
            )}
            <FormPopup />
        </Popup>
    );
};

export default InhouseMapPopup;
